import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message as antMessage,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  StopOutlined,
} from "@ant-design/icons";

import {
  createOrgInvite,
  deleteOrgInvite,
  fetchOrgInvites,
  fetchOrgUsers,
  setUserAccess,
  updateOrgUser,
} from "../services/assignmentService";

const { Text, Paragraph } = Typography;

const FEATURE_COLUMNS = [
  {
    key: "edit_prompts",
    title: "Edit master prompt",
    hint: "Change the shared prompt every ad is analyzed against. Affects everyone.",
  },
  {
    key: "edit_knowledge",
    title: "Edit knowledge",
    hint: "Add or remove knowledge-base entries and brand assets.",
  },
  {
    key: "bulk_analyze",
    title: "Bulk analyze",
    hint: "Analyze more than one ad at a time. Off = one ad at a time (cost control).",
  },
  {
    key: "manage_users",
    title: "Manage users",
    hint: "Approve signups and toggle permissions. Cannot grant admin, and cannot reach API keys.",
  },
];

// Which tabs each person sees in the top nav.
const TAB_COLUMNS = [
  { key: "web", title: "The Web" },
  { key: "creatives", title: "Creatives" },
  { key: "post", title: "Copy" },
  { key: "assignment", title: "Assignment" },
  { key: "neuro", title: "Neuro" },
];

/**
 * Team management: who's on the team, who's waiting to get in, and who's invited.
 *
 * API keys deliberately have no permission flag — they are admin-only and can't
 * be delegated, so nothing in this panel can hand them out by accident.
 */
export default function TeamPermissions({ isAdmin }) {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [revoked, setRevoked] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState("");
  const [messageApi, contextHolder] = antMessage.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [team, inv] = await Promise.all([
        fetchOrgUsers(),
        fetchOrgInvites().catch(() => ({ invites: [] })),
      ]);
      setUsers(team?.users || []);
      setPending(team?.pending || []);
      setRevoked(team?.revoked || []);
      setInvites(inv?.invites || []);
    } catch (err) {
      setError(err?.payload?.error || err?.message || "Couldn't load the team.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (user, action) => {
    setSavingId(user.id);
    try {
      await setUserAccess(user.id, action);
      messageApi.success(
        action === "approve"
          ? `${user.email} is on the team.`
          : action === "deny"
            ? `${user.email} was denied.`
            : `${user.email} no longer has access.`,
      );
      await load();
    } catch (err) {
      messageApi.error(err?.payload?.error || err?.message || "Couldn't save.");
    } finally {
      setSavingId(null);
    }
  };

  const toggleFeature = async (user, flag, value) => {
    setSavingId(user.id);
    const nextFeatures = { ...user.features, [flag]: value };
    setUsers((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, features: nextFeatures } : u)),
    );
    try {
      const res = await updateOrgUser({ id: user.id, features: nextFeatures });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? res.user : u)));
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? user : u)));
      messageApi.error(err?.payload?.error || err?.message || "Couldn't save.");
    } finally {
      setSavingId(null);
    }
  };

  const toggleTab = async (user, tab, value) => {
    setSavingId(user.id);
    const nextTabs = { ...user.tabs, [tab]: value };
    setUsers((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, tabs: nextTabs } : u)),
    );
    try {
      const res = await updateOrgUser({ id: user.id, tabs: nextTabs });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? res.user : u)));
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? user : u)));
      messageApi.error(err?.payload?.error || err?.message || "Couldn't save.");
    } finally {
      setSavingId(null);
    }
  };

  const toggleAdmin = async (user, value) => {
    setSavingId(user.id);
    try {
      const res = await updateOrgUser({ id: user.id, isAdmin: value });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? res.user : u)));
      messageApi.success(
        value
          ? `${user.email} is now an admin and can manage API keys.`
          : `${user.email} is no longer an admin.`,
      );
    } catch (err) {
      messageApi.error(err?.payload?.error || err?.message || "Couldn't save.");
    } finally {
      setSavingId(null);
    }
  };

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    try {
      const res = await createOrgInvite({ email });
      setInviteEmail("");
      messageApi.success(res?.hint || `Invited ${email}.`);
      await load();
    } catch (err) {
      messageApi.error(err?.payload?.error || err?.message || "Couldn't invite.");
    } finally {
      setInviting(false);
    }
  };

  const revokeInvite = async (email) => {
    try {
      await deleteOrgInvite(email);
      messageApi.success(`Invitation to ${email} withdrawn.`);
      await load();
    } catch (err) {
      messageApi.error(err?.payload?.error || err?.message || "Couldn't withdraw.");
    }
  };

  const memberCell = (_, user) => (
    <Space direction="vertical" size={0}>
      <Space size={6}>
        <Text strong>{user.name || user.email}</Text>
        {user.isAdmin ? (
          <Tag color="purple" icon={<KeyOutlined />}>
            Admin
          </Tag>
        ) : null}
      </Space>
      {user.name ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {user.email}
        </Text>
      ) : null}
    </Space>
  );

  const teamColumns = [
    { title: "Member", key: "email", fixed: "left", width: 220, render: memberCell },
    {
      title: "Permissions",
      children: FEATURE_COLUMNS.map((col) => ({
        title: (
          <Tooltip title={col.hint}>
            <span style={{ fontSize: 12 }}>{col.title}</span>
          </Tooltip>
        ),
        key: col.key,
        align: "center",
        width: 110,
        render: (_, user) => (
          <Switch
            size="small"
            checked={Boolean(user.features?.[col.key])}
            disabled={user.isAdmin || savingId === user.id}
            onChange={(value) => toggleFeature(user, col.key, value)}
          />
        ),
      })),
    },
    {
      title: "Visible tabs",
      children: TAB_COLUMNS.map((col) => ({
        title: <span style={{ fontSize: 12 }}>{col.title}</span>,
        key: `tab_${col.key}`,
        align: "center",
        width: 96,
        render: (_, user) => (
          <Switch
            size="small"
            checked={user.tabs?.[col.key] !== false}
            disabled={user.isAdmin || savingId === user.id}
            onChange={(value) => toggleTab(user, col.key, value)}
          />
        ),
      })),
    },
    ...(isAdmin
      ? [
          {
            title: (
              <Tooltip title="Admins can view and edit API keys. This is the only way to reach them.">
                <span style={{ fontSize: 12 }}>
                  <LockOutlined /> Admin
                </span>
              </Tooltip>
            ),
            key: "is_admin",
            align: "center",
            width: 90,
            render: (_, user) => (
              <Switch
                size="small"
                checked={user.isAdmin}
                disabled={savingId === user.id}
                onChange={(value) => toggleAdmin(user, value)}
              />
            ),
          },
        ]
      : []),
    {
      title: "",
      key: "revoke",
      align: "right",
      width: 110,
      render: (_, user) =>
        user.isAdmin ? null : (
          <Popconfirm
            title="Remove from the team?"
            description="They lose access to the shared API keys, knowledge, and prompts immediately."
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={() => act(user, "revoke")}
          >
            <Button size="small" danger type="text" icon={<StopOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        ),
    },
  ];

  const pendingColumns = [
    { title: "Person", key: "email", render: memberCell },
    {
      title: "Requested",
      key: "requested",
      width: 170,
      render: (_, u) =>
        u.requestedAt || u.createdAt ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {new Date(u.requestedAt || u.createdAt).toLocaleString()}
          </Text>
        ) : null,
    },
    {
      title: "",
      key: "actions",
      align: "right",
      width: 200,
      render: (_, user) => (
        <Space>
          <Button
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            loading={savingId === user.id}
            onClick={() => act(user, "approve")}
          >
            Approve
          </Button>
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            loading={savingId === user.id}
            onClick={() => act(user, "deny")}
          >
            Deny
          </Button>
        </Space>
      ),
    },
  ];

  const inviteColumns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email, row) => (
        <Space size={6}>
          <Text>{email}</Text>
          {row.pending ? (
            <Tag>Awaiting signup</Tag>
          ) : (
            <Tag color="success">Joined</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      width: 130,
      render: (_, row) =>
        row.pending ? (
          <Button size="small" type="text" danger onClick={() => revokeInvite(row.email)}>
            Withdraw
          </Button>
        ) : null,
    },
  ];

  if (loading) {
    return (
      <Card className="panel-card" title="Team & access">
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin />
        </div>
      </Card>
    );
  }

  return (
    <Card className="panel-card" title="Team & access">
      {contextHolder}

      <Alert
        type="info"
        showIcon
        icon={<LockOutlined />}
        style={{ marginBottom: 16 }}
        message="How people get in"
        description="Invite an email first — then that person can create an account and they're in immediately. Uninvited emails can't sign up at all (nothing is even written to the database). The Requests tab only shows accounts that predate this policy. API keys are admin-only and have no permission flag, so nothing here can hand them out."
      />

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}

      <Tabs
        defaultActiveKey={pending.length ? "pending" : "team"}
        items={[
          {
            key: "pending",
            label: (
              <Badge count={pending.length} offset={[10, -2]} size="small">
                <span>Requests</span>
              </Badge>
            ),
            children: pending.length ? (
              <Table
                rowKey="id"
                size="small"
                columns={pendingColumns}
                dataSource={pending}
                pagination={false}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nobody's waiting for access."
              />
            ),
          },
          {
            key: "team",
            label: `Team (${users.length})`,
            children: (
              <Table
                rowKey="id"
                size="small"
                columns={teamColumns}
                dataSource={users}
                pagination={false}
                scroll={{ x: 1240 }}
              />
            ),
          },
          {
            key: "invites",
            label: "Invites",
            children: (
              <>
                <Space.Compact style={{ width: "100%", marginBottom: 14 }}>
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="teammate@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onPressEnter={invite}
                  />
                  <Button type="primary" loading={inviting} onClick={invite}>
                    Invite
                  </Button>
                </Space.Compact>

                <Paragraph type="secondary" style={{ fontSize: 12 }}>
                  Invited people are approved automatically on first login, with the
                  default permissions (work yes, edit the master prompt no). Adjust
                  them on the Team tab afterwards.
                </Paragraph>

                {invites.length ? (
                  <Table
                    rowKey="email"
                    size="small"
                    columns={inviteColumns}
                    dataSource={invites}
                    pagination={false}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No invites yet."
                  />
                )}
              </>
            ),
          },
          ...(revoked.length
            ? [
                {
                  key: "revoked",
                  label: `No access (${revoked.length})`,
                  children: (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={revoked}
                      columns={[
                        { title: "Person", key: "email", render: memberCell },
                        {
                          title: "Status",
                          dataIndex: "status",
                          key: "status",
                          width: 120,
                          render: (s) => <Tag color="red">{s}</Tag>,
                        },
                        {
                          title: "",
                          key: "actions",
                          align: "right",
                          width: 140,
                          render: (_, user) => (
                            <Button
                              size="small"
                              icon={<CheckOutlined />}
                              loading={savingId === user.id}
                              onClick={() => act(user, "approve")}
                            >
                              Restore
                            </Button>
                          ),
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />
    </Card>
  );
}
